import {useCallback} from 'react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import {goIcon, childSeatIcon, smallArrowRight} from '@img/index.js'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import useScrollOnError from '@/customComponents/uiComponents/modal/useScrollOnError.js'
import Counter from '@/customComponents/uiComponents/global/NumberCounter.jsx'

export default function PassengerSection({
    tripId, passengers, committedPassengers = {}, setPassengers,
    childSeats, committedChildSeats = {}, setChildSeats,
    passengerTypes = [], childSeatTypes = [],
    onDone, onClose, isLoading, error, view = 'main', onViewChange, only = null,
    passengerTypesPolicy = {canEdit: true, blockedReason: null},
    childSeatsPolicy = {canEdit: true, blockedReason: null}
}) {
    useScrollOnError(error)
    const hasChanges = (() => {
        const allPKeys = new Set([...Object.keys(passengers), ...Object.keys(committedPassengers)])
        for (const k of allPKeys) if ((passengers[k] || 0) !== (committedPassengers[k] || 0)) return true
        const allSKeys = new Set([...Object.keys(childSeats), ...Object.keys(committedChildSeats)])
        for (const k of allSKeys) if ((childSeats[k] || 0) !== (committedChildSeats[k] || 0)) return true
        return false
    })()

    const totalPassengers = Object.values(passengers).reduce((s, c) => s + c, 0)

    /*
     * The rule is at least one ADULT, not at least one passenger — "1 infant, 0 adults"
     * is not a ride anyone can take, and it used to pass this gate.
     *
     * Identified BY LABEL, the same way the booking flow's own passenger step does it
     * (plugin `modalPages/passengerQuantity.jsx`): /config/passenger-types carries no
     * "is adult" flag, so the label is all there is to go on. If no row matches — the
     * type renamed, or the list not loaded yet — fall back to the old any-passenger rule
     * rather than disabling Done with nothing the user could do to satisfy it.
     */
    const adultTypeId = passengerTypes.find(t => t.label?.toLowerCase().includes('adult'))?.id
    const passengersInvalid = adultTypeId != null
        ? (passengers[adultTypeId] || 0) === 0
        : totalPassengers === 0

    const handlePassengerChange = useCallback((typeId, delta) => {
        setPassengers(prev => ({...prev, [typeId]: Math.max(0, (prev[typeId] || 0) + delta)}))
    }, [setPassengers])

    const handleSeatChange = useCallback((typeId, delta) => {
        setChildSeats(prev => ({...prev, [typeId]: Math.max(0, (prev[typeId] || 0) + delta)}))
    }, [setChildSeats])

    // Seat detail view
    if (view === 'seatDetails') {
        return (
            <div className="bg-white hopn-view-in">
                <div className="edit-swiper-pagination pagination-below flex relative max-w-full [&_.swiper-pagination]:flex [&_.swiper-pagination]:items-center [&_.swiper-pagination]:justify-center [&_.swiper-pagination]:gap-1.5 [&_.swiper-pagination-bullet]:w-2 [&_.swiper-pagination-bullet]:h-2 [&_.swiper-pagination-bullet]:inline-block [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:m-0 max-md:w-[calc(100%+16px)]">
                    <Swiper
                        modules={[Pagination]}
                        spaceBetween={15}
                        pagination={{clickable: true}}
                        breakpoints={{
                            0: {slidesPerView: 1.5, spaceBetween: 8},
                            991: {slidesPerView: 1.5},
                        }}
                    >
                        {childSeatTypes.map(item => (
                            <SwiperSlide key={item.id}>
                                <div className="shrink-0 rounded-3xl pt-4 pr-2.5 pb-2 pl-[15px] text-center relative border border-(--bg-dark-grey)">
                                    <div className="relative rounded-lg mb-[21px] flex flex-col items-center text-3xl min-h-[70px]">
                                        <img src={item.image_url} alt={item.label} className="w-full"/>
                                        <h5 className="m-0 text-xl font-thin mb-5 max-w-full pr-0">{item.label}</h5>
                                        {item.description && <p className="text-sm">{item.description}</p>}
                                    </div>
                                    <div className="flex justify-between items-center bg-(--bg-grey) rounded-full border border-[#ccc] py-0.5 px-1.25">
                                        <Counter
                                            value={childSeats[item.id] || 0}
                                            onDecrement={() => handleSeatChange(item.id, -1)}
                                            onIncrement={() => handleSeatChange(item.id, 1)}
                                            buttonColor="white"
                                        />
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        )
    }

    // Main passenger view
    return (
        <div className="hopn-view-in">
            {only !== 'childseats' && (<>
            {/* Passenger Types List */}
            <div className="mb-10 relative">
                {passengerTypes.length === 0 ? (
                    <center><Loader/></center>
                ) : (
                    passengerTypes.map(type => (
                        <div key={type.id} className="flex items-end justify-between mb-5">
                            <div className="flex items-center">
                                <span className="text-[28px] mr-4 inline-flex">
                                    <img src={type.image_url || goIcon} alt={type.label} className="w-auto max-h-[51px]"/>
                                </span>
                            </div>
                            <div className="flex items-center justify-between w-[90%] border-b border-[#EAEAEA] leading-none pb-[3px]">
                                <div>
                                    <span className="block text-xl mb-0.5">{type.label}</span>
                                    <span className="text-[#8d8e8f] text-sm mb-0 inline-block leading-normal">{type.description || ''}</span>
                                </div>
                                <div className="flex items-center">
                                    <Counter
                                        value={passengers[type.id] || 0}
                                        onDecrement={() => handlePassengerChange(type.id, -1)}
                                        onIncrement={() => handlePassengerChange(type.id, 1)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))
                )}
                {!passengerTypesPolicy.canEdit && (
                    <div className="absolute inset-0 bg-white/70 z-10 rounded-(--radius-2xl) flex items-center justify-center p-4">
                        {passengerTypesPolicy.blockedReason && (
                            <p className="mb-0! text-center text-sm text-(--text-primary) bg-white px-4 py-2 rounded-xl shadow">{passengerTypesPolicy.blockedReason}</p>
                        )}
                    </div>
                )}
            </div>

            {passengersInvalid && (
                <p className="text-red-500 text-sm mt-2">
                    {adultTypeId != null ? 'Please select at least 1 Adult.' : 'Please select at least 1 passenger.'}
                </p>
            )}
            </>)}

            {/* Child Car Seat Section */}
            {only !== 'passengers' && (
            <div className="relative">
                {/* No "Add Child Car Seat & Stroller" heading here — the modal's own title
                    already says Child Seats, so it was saying the same thing twice. Removed
                    from the booking step's passengerQuantity for the same reason. */}
                <div className="hopn-carseat-header">
                    <span className="hopn-free-tag">Free</span>
                    <button type="button" onClick={() => onViewChange?.('seatDetails')}
                            className="text-base font-medium no-underline flex items-center border-0 bg-transparent text-[#6086c1] p-0 cursor-pointer">
                        Found right seat <img src={smallArrowRight} alt="Arrow right" className="ml-3"/>
                    </button>
                </div>

                <div className="edit-swiper-pagination flex relative max-w-full [&_.swiper]:pb-10 [&_.swiper-pagination]:flex [&_.swiper-pagination]:items-center [&_.swiper-pagination]:justify-center [&_.swiper-pagination]:gap-1.5 [&_.swiper-pagination]:bottom-0 [&_.swiper-pagination-bullet]:w-2 [&_.swiper-pagination-bullet]:h-2 [&_.swiper-pagination-bullet]:inline-block [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:m-0 max-md:w-[calc(100%+16px)]">
                    <Swiper
                        modules={[Pagination]}
                        spaceBetween={15}
                        pagination={{clickable: true}}
                        breakpoints={{
                            0: {slidesPerView: 2.3, spaceBetween: 8},
                            991: {slidesPerView: 4.4},
                        }}
                    >
                        {childSeatTypes.map(item => (
                            <SwiperSlide key={item.id}>
                                <div className="shrink-0 rounded-3xl pt-4 pr-2.5 pb-2 pl-[15px] text-center relative border border-(--bg-dark-grey)">
                                    <div className="relative rounded-lg mb-5.25 flex justify-start text-3xl min-h-[70px] max-md:mb-2.5">
                                        <h5 className="m-0 text-left text-base font-thin max-w-[83px]">{item.label}</h5>
                                        <img src={item.image_url || childSeatIcon} alt={item.label} className="absolute w-auto h-17.5 -right-2.5 max-md:h-14"/>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <Counter
                                            value={childSeats[item.id] || 0}
                                            onDecrement={() => handleSeatChange(item.id, -1)}
                                            onIncrement={() => handleSeatChange(item.id, 1)}
                                        />
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                {!childSeatsPolicy.canEdit && (
                    <div className="absolute inset-0 bg-white/70 z-10 rounded-(--radius-2xl) flex items-center justify-center p-4">
                        {childSeatsPolicy.blockedReason && (
                            <p className="mb-0! text-center text-sm text-(--text-primary) bg-white px-4 py-2 rounded-xl shadow">{childSeatsPolicy.blockedReason}</p>
                        )}
                    </div>
                )}
            </div>
            )}

            {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

            <ModalFooter>
                <button type="button"
                        onClick={onDone}
                        disabled={(only !== 'childseats' && passengersInvalid) || !hasChanges || isLoading}
                        className="w-full py-[15px] rounded-full bg-(--text-primary) text-white text-base border-0 cursor-pointer flex items-center justify-center min-h-[55px] disabled:opacity-50">
                    {isLoading ? <Loader color="#fff"/> : 'Done'}
                </button>
            </ModalFooter>
        </div>
    )
}
